// External gating: prefer internal staff; only allow externals when necessary
// For each (event, role), we add:
// 1) sum x_ext(E,R) - req(E,R) * z_ext(E,R) <= 0
// 2) - sum x_int(E,R) - d(E,R) - req(E,R) * z_ext(E,R) <= - req(E,R)
// Requires: staffRequiredByRole, decisionVariables, eventRoleDecisionVariables, externalGatingDecisionVariables, employmentByUser
export function buildExternalGatingConstraints(model, _params = {}) {
  const idx = model?.optimizationIndexes || {};
  const staffRequiredByRole = idx?.staffRequiredByRole || {};
  const decisionVars = idx?.decisionVariables || {};
  const dVars = idx?.eventRoleDecisionVariables || {};
  const zVars = idx?.externalGatingDecisionVariables || {};
  const employmentByUser = idx?.employmentByUser || {};

  const declaredD = new Set(Object.keys(dVars));
  const declaredZ = new Set(Object.keys(zVars));

  // Group decision variables by (E,R) and split internal vs external
  const byER = new Map(); // key `${eKey}_${rKey}` -> { ext: [xNames], int: [xNames], req, dName, zName }
  for (const [xName, meta] of Object.entries(decisionVars)) {
    const eKey = meta?.eventKey;
    const rKey = meta?.roleKey;
    const uKey = meta?.userKey;
    if (!eKey || !rKey || !uKey) continue;
    const key = `${eKey}_${rKey}`;
    if (!byER.has(key)) byER.set(key, { ext: [], int: [], req: 0, dName: `d_${eKey}_${rKey}`, zName: `z_ext_${eKey}_${rKey}` });
    const bucket = employmentByUser[uKey] === 'external' ? 'ext' : 'int';
    byER.get(key)[bucket].push(xName);
  }

  for (const [eKey, rolesObj] of Object.entries(staffRequiredByRole)) {
    for (const [rKey, reqValRaw] of Object.entries(rolesObj || {})) {
      const reqVal = Number(reqValRaw || 0);
      const key = `${eKey}_${rKey}`;
      if (!byER.has(key)) byER.set(key, { ext: [], int: [], req: reqVal, dName: `d_${eKey}_${rKey}`, zName: `z_ext_${eKey}_${rKey}` });
      else byER.get(key).req = reqVal;
    }
  }

  const constraints = [];
  for (const [, pack] of byER.entries()) {
    const { ext, int, req, dName, zName } = pack;
    const hasZ = declaredZ.has(zName);
    const hasD = declaredD.has(dName);
    if (!hasZ) continue;

    // 1) sum x_ext - req * z <= 0
    if (ext.length) {
      const vars = [...ext, zName];
      const coeffs = [...ext.map(() => 1), -req];
      constraints.push({ linear: { vars, coeffs }, domain: [-100000, 0] });
    } else {
      // No external vars: still ensure z is not needed by tying to req? Not necessary; skip.
    }

    // 2) - sum x_int - d - req * z <= -req
    // fill internals with -1
    const vars_int = int;
    const coeffs_int = int.map(() => -1);
    const vars = [...vars_int];
    const coeffs = [...coeffs_int];
    if (hasD) {
      vars.push(dName);
      coeffs.push(-1);
    }
    vars.push(zName);
    coeffs.push(-req);
    constraints.push({ linear: { vars, coeffs }, domain: [-100000, -req] });
  }

  return constraints;
}
